import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, X, FileVideo, Image as ImageIcon, CheckCircle2, ArrowRight, ArrowLeft, Copy } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import { SERVICES, BUSINESS } from "@/content/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Contact", "Move Details", "Photos & Video", "Review"];
const MAX_SIZE = 25 * 1024 * 1024;
const MAX_FILES = 8;
const HOME_SIZES = ["Studio / 1 BR", "2 Bedrooms", "3 Bedrooms", "4+ Bedrooms", "Office / Commercial", "Storage Unit", "Other"];
const CONTACT_METHODS = [
  { value: "phone", label: "Call" },
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
];

const inputCls =
  "w-full border border-white/10 bg-navy-900 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 transition-colors focus:border-gold focus:outline-none";
const labelCls = "mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-400";

const Field = ({ label, id, error, children }) => (
  <div>
    <label htmlFor={id} className={labelCls}>{label}</label>
    {children}
    {error && <p data-testid={`${id}-error`} className="mt-1.5 text-xs text-red-400">{error}</p>}
  </div>
);

export default function Quote() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_contact: "phone",
    service: params.get("service") || "",
    origin: "",
    destination: "",
    move_date: "",
    home_size: "",
    details: "",
  });
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addFiles = (list) => {
    const valid = [];
    for (const f of Array.from(list)) {
      if (!/^(image|video)\//.test(f.type)) {
        toast.error(`${f.name} must be a photo or video.`);
        continue;
      }
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name} is over the 25MB limit.`);
        continue;
      }
      valid.push(f);
    }
    setFiles((prev) => {
      const next = [...prev, ...valid].slice(0, MAX_FILES);
      if (prev.length + valid.length > MAX_FILES) toast.error(`Up to ${MAX_FILES} files allowed.`);
      return next;
    });
  };

  const validate = () => {
    const errs = {};
    if (step === 0) {
      if (!form.name.trim()) errs.name = "Please tell us your name.";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = "Please enter a valid email address.";
    }
    if (step === 1 && !form.service) errs.service = "Please choose a service.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, 3));
    window.scrollTo({ top: 0 });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const res = await axios.post(`${API}/quote`, fd);
      setDone(res.data);
      toast.success("Quote request sent — we'll be in touch shortly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <>
        <Seo title="Quote Request Received | Home Team Moving Co." siteName="Home Team Moving Co." description="Your quote request has been received." />
        <section className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6 lg:py-32">
          <FadeUp>
            <CheckCircle2 data-testid="quote-success-icon" className="mx-auto h-16 w-16 text-gold" />
            <h1 className="mt-8 font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
              Request received.
            </h1>
            <p className="mt-5 text-slate-300">
              Thanks, {done && form.name.split(" ")[0]}. Your request {done.attachments > 0 && `and ${done.attachments} file${done.attachments > 1 ? "s" : ""} `} landed with our crew — a confirmation is on its way to your inbox, and we'll follow up with your estimate.
            </p>
            <div className="mx-auto mt-8 flex max-w-md items-center justify-between gap-3 border border-gold/30 bg-navy-900 px-5 py-4">
              <div className="text-left">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">Your reference</p>
                <p data-testid="quote-reference-id" className="font-mono text-sm text-gold">{done.id}</p>
              </div>
              <button
                data-testid="copy-reference-btn"
                onClick={() => {
                  navigator.clipboard?.writeText(done.id);
                  toast.success("Reference copied.");
                }}
                className="p-2 text-slate-400 transition-colors hover:text-gold"
                aria-label="Copy reference number"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
            <Link to="/" data-testid="quote-success-home-link" className="mt-10 inline-block font-display text-sm font-bold uppercase tracking-[0.25em] text-gold transition-colors hover:text-gold-light">
              Back to home
            </Link>
          </FadeUp>
        </section>
      </>
    );
  }

  return (
    <>
      <Seo
        title="Request a Free Quote | Home Team Moving Co."
        siteName="Home Team Moving Co."
        description="Get a fast, honest estimate for moving, hauling, cleanouts, and more. Send photos or a video walkthrough and we'll price your job."
      />
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
          <FadeUp>
            <p className="eyebrow mb-4">Free Estimate · No Obligation</p>
            <h1 className="max-w-3xl font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">
              Request a <span className="text-gold">quote.</span>
            </h1>
            <p className="mt-5 max-w-2xl leading-relaxed text-slate-300">
              Four quick steps. Snap photos or a short video of the job and we'll come back with a straight, honest price.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:py-20">
        <ol data-testid="quote-progress" className="mb-12 flex items-center gap-0" aria-label="Quote form progress">
          {STEPS.map((label, i) => (
            <li key={label} className="flex flex-1 items-center last:flex-none">
              <div className="flex flex-col items-center gap-2">
                <span className={`flex h-9 w-9 items-center justify-center border font-display text-sm font-bold transition-colors duration-300 ${i <= step ? "border-gold bg-gold text-navy-950" : "border-white/20 text-slate-500"}`}>
                  {i + 1}
                </span>
                <span className={`hidden text-[10px] font-semibold uppercase tracking-[0.2em] sm:block ${i <= step ? "text-gold" : "text-slate-500"}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && <span className={`mx-2 h-px flex-1 transition-colors duration-300 ${i < step ? "bg-gold" : "bg-white/15"}`} />}
            </li>
          ))}
        </ol>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35 }}
            className="border border-white/10 bg-navy-900/50 p-6 sm:p-10"
          >
            {step === 0 && (
              <div data-testid="quote-form-step-1" className="space-y-6">
                <Field label="Full name *" id="quote-name" error={errors.name}>
                  <input id="quote-name" data-testid="quote-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Rodriguez" autoComplete="name" />
                </Field>
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label="Email *" id="quote-email" error={errors.email}>
                    <input id="quote-email" data-testid="quote-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" autoComplete="email" />
                  </Field>
                  <Field label="Phone" id="quote-phone">
                    <input id="quote-phone" data-testid="quote-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" autoComplete="tel" />
                  </Field>
                </div>
                <div>
                  <span className={labelCls} id="contact-method-label">Preferred contact method</span>
                  <div className="flex gap-2" role="radiogroup" aria-labelledby="contact-method-label">
                    {CONTACT_METHODS.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        role="radio"
                        aria-checked={form.preferred_contact === m.value}
                        data-testid={`contact-method-${m.value}`}
                        onClick={() => setForm((f) => ({ ...f, preferred_contact: m.value }))}
                        className={`flex-1 border px-4 py-3 font-display text-sm font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${form.preferred_contact === m.value ? "border-gold bg-gold text-navy-950" : "border-white/15 text-slate-300 hover:border-gold/50"}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div data-testid="quote-form-step-2" className="space-y-6">
                <Field label="Service needed *" id="quote-service" error={errors.service}>
                  <select id="quote-service" data-testid="quote-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                  </select>
                </Field>
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label="Moving from (city / zip)" id="quote-origin">
                    <input id="quote-origin" data-testid="quote-origin-input" className={inputCls} value={form.origin} onChange={set("origin")} placeholder="Anaheim, CA" />
                  </Field>
                  <Field label="Moving to (city / zip)" id="quote-destination">
                    <input id="quote-destination" data-testid="quote-destination-input" className={inputCls} value={form.destination} onChange={set("destination")} placeholder="Irvine, CA" />
                  </Field>
                </div>
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label="Preferred date" id="quote-date">
                    <input id="quote-date" data-testid="quote-date-input" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.move_date} onChange={set("move_date")} />
                  </Field>
                  <Field label="Home / space size" id="quote-size">
                    <select id="quote-size" data-testid="quote-size-select" className={inputCls} value={form.home_size} onChange={set("home_size")}>
                      <option value="">Select size…</option>
                      {HOME_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="quote-form-step-3" className="space-y-6">
                <div
                  data-testid="quote-upload-zone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    addFiles(e.dataTransfer.files);
                  }}
                  onClick={() => inputRef.current?.click()}
                  onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload photos or videos of the job"
                  className="cursor-pointer border border-dashed border-gold/40 bg-navy-950/60 px-6 py-12 text-center transition-colors duration-300 hover:border-gold"
                >
                  <UploadCloud className="mx-auto h-10 w-10 text-gold" />
                  <p className="mt-4 font-display text-lg font-bold uppercase tracking-wide text-white">
                    Drop photos or a video walkthrough
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    Up to {MAX_FILES} files · JPG, PNG, HEIC, MP4, MOV · 25MB each
                  </p>
                  <input
                    ref={inputRef}
                    data-testid="quote-file-input"
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={(e) => {
                      addFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </div>
                {files.length > 0 && (
                  <ul data-testid="quote-file-list" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="group relative border border-white/10 bg-navy-950 p-2">
                        {f.type.startsWith("image/") ? (
                          <img src={URL.createObjectURL(f)} alt={f.name} className="h-20 w-full object-cover" />
                        ) : (
                          <span className="flex h-20 w-full items-center justify-center bg-navy-800">
                            <FileVideo className="h-6 w-6 text-gold" />
                          </span>
                        )}
                        <span className="mt-1.5 flex items-center gap-1 truncate text-[10px] text-slate-400">
                          <ImageIcon className="h-3 w-3 shrink-0" /> {f.name}
                        </span>
                        <button
                          type="button"
                          data-testid={`remove-file-${i}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFiles((prev) => prev.filter((_, idx) => idx !== i));
                          }}
                          className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white"
                          aria-label={`Remove ${f.name}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <Field label="Anything else we should know?" id="quote-details">
                  <textarea id="quote-details" data-testid="quote-details-input" rows={4} className={inputCls} value={form.details} onChange={set("details")} placeholder="Stairs, elevators, heavy items, timing constraints…" />
                </Field>
              </div>
            )}

            {step === 3 && (
              <div data-testid="quote-form-step-4">
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Review your request</h2>
                <dl className="mt-6 divide-y divide-white/10 text-sm">
                  {[
                    ["Name", form.name],
                    ["Email", form.email],
                    ["Phone", form.phone || "—"],
                    ["Preferred contact", CONTACT_METHODS.find((m) => m.value === form.preferred_contact)?.label],
                    ["Service", SERVICES.find((s) => s.title === form.service)?.title || form.service],
                    ["From", form.origin || "—"],
                    ["To", form.destination || "—"],
                    ["Preferred date", form.move_date || "Flexible"],
                    ["Size", form.home_size || "—"],
                    ["Files attached", `${files.length}`],
                    ["Details", form.details || "—"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 py-3">
                      <dt className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{k}</dt>
                      <dd className="text-right text-slate-200">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-6 text-xs leading-relaxed text-slate-500">
                  By submitting, you agree to our <Link to="/terms" className="text-gold underline">Terms</Link> and <Link to="/privacy" className="text-gold underline">Privacy Policy</Link>. We'll only use your details to prepare your estimate.
                </p>
              </div>
            )}

            <div className="mt-10 flex items-center justify-between gap-4">
              {step > 0 ? (
                <button
                  type="button"
                  data-testid="quote-back-btn"
                  onClick={() => setStep((s) => s - 1)}
                  className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 font-display text-sm font-bold uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-gold hover:text-gold"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
              ) : (
                <span />
              )}
              {step < 3 ? (
                <button
                  type="button"
                  data-testid="quote-next-btn"
                  onClick={next}
                  className="inline-flex items-center gap-2 bg-gold px-8 py-3 font-display text-sm font-bold uppercase tracking-[0.2em] text-navy-950 transition-colors hover:bg-gold-light"
                >
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="quote-form-submit-btn"
                  onClick={submit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-gold px-8 py-3 font-display text-sm font-bold uppercase tracking-[0.2em] text-navy-950 transition-colors hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send my request"} <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <p className="mt-8 text-center text-xs text-slate-500">
          Prefer to talk? Call <a href={BUSINESS.phoneHref} className="text-gold">{BUSINESS.phone}</a> or email <a href={BUSINESS.emailHref} className="text-gold">{BUSINESS.email}</a>
        </p>
      </section>
    </>
  );
}
